import { tagsApi } from 'api/tags/tags.api';

// Lazy import to break circular dependency: tags.actions ↔ projects.actions
const getFetchProjectId = () =>
  import('redux/modules/projects.actions').then(m => m.fetchProjectId);

import { normalizeTag, normalizeTagList } from 'redux/modules/tags.reducer';
import { getTagEntities } from 'redux/modules/tags.selectors';
import type { TagDraft, TagNormalized } from 'redux/modules/tags.types';
import {
  clearTagDraft,
  createTagDraft,
  errorSavingTag,
  receiveTags,
  savedTag,
  tagDeleted,
  updateTagDraft,
  updateTags,
  wrapSaving,
} from 'redux/modules/tags.types';
import { showToast } from 'redux/modules/toasts.actions';
import { getAllLoadedProjects } from 'redux/selectors/projects';
import type { Thunk } from 'types/redux';
import type { Tag } from 'types/tag';
import { fetchPaginated } from 'utils/api';
import debug from 'utils/debug';
import { genericResequencer } from 'utils/resequencer';
import { notNull } from 'utils/typesafe';

export const initializeTagDraft = (draft?: Partial<TagDraft>) => {
  const draftWithSequence = { sequence: 0, ...draft };
  return createTagDraft(draftWithSequence);
};

export { clearTagDraft, updateTagDraft };

export const saveTag = (
  tagDraft: TagDraft,
  clearDraft: boolean = true,
  addChildTag: boolean = true
): Thunk<Promise<TagNormalized>> => {
  return async dispatch => {
    const saveType = tagDraft.id != null ? 'updating' : 'creating';
    const data = await dispatch(
      wrapSaving(tagsApi.save(tagDraft), String(tagDraft?.id ?? 'draft'))
    );

    const isTopLevel = tagDraft.parent_id == null;
    const normalizedData = normalizeTag(data);
    const createdTagId = normalizedData.result;

    dispatch(savedTag(normalizedData));

    if (clearDraft) {
      dispatch(clearTagDraft());
    }

    if (addChildTag && saveType === 'creating') {
      // Automatically add new child tag when creating a top level tag group
      // Automatically add a new sibling tab when creating a new leaf level tag
      const parentOfNewTag = isTopLevel ? createdTagId : tagDraft.parent_id;
      dispatch(
        initializeTagDraft({
          parent_id: parentOfNewTag,
          sequence: 0,
        })
      );
    }

    return normalizedData.entities.tag[createdTagId];
  };
};

export const fetchTags =
  (options?: { filter?: 'active' | 'all' | 'archived' }): Thunk<Promise<boolean>> =>
  async dispatch => {
    try {
      dispatch(receiveTags.started());
      const perPage = 500;
      const data = await fetchPaginated<Tag>({ perPage }, page =>
        tagsApi.getAll({ ...options, page, perPage })
      );
      const normalizedData = normalizeTagList(data);
      dispatch(receiveTags.done({ result: normalizedData }));
      return true;
    } catch (err) {
      dispatch(receiveTags.failed(err));
      throw err;
    }
  };

export const fetchSingleTag =
  (tagId: number): Thunk<Promise<unknown>> =>
  async dispatch => {
    const tag = await tagsApi.get(tagId);
    if (tag.parent_id != null) {
      // With how the normalization currently works,
      // we don't want to insert a leaf-level tag directly into the store,
      // because then the tag id would also be added to the `result` array, which
      // would render it as a tag group.
      // TODO: refactor state to not be normalized in this way or something
      return dispatch(fetchSingleTag(tag.parent_id));
    }
    const normalizedData = normalizeTagList([tag]);
    dispatch(updateTags(normalizedData));
  };

export const acceptTagDeleted =
  (tagId: number): Thunk =>
  dispatch => {
    if (tagId != null) {
      debug.warn(
        'TODO: Tag pusher notifications improved, alternative branch in acceptTagDeleted can be removed'
      );
      dispatch(tagDeleted(tagId));
    } else {
      // Alternative implementation for now (since tagId can be null right now):
      // If a tag is deleted, we don't know which one, so we refetch all tags
      dispatch(fetchTags());
    }
  };

export const sortTag =
  (options: { source: Tag | TagDraft; target: Tag | TagDraft; save?: boolean }): Thunk =>
  async (dispatch, getState) => {
    const tagEntities = getTagEntities(getState());
    const tagChildIds = tagEntities[options.source.parent_id!].children;
    const tags = tagChildIds.map(t => tagEntities[t]);

    if (!options.source.sequence || !options.target.sequence) {
      return;
    }

    const updatedTags = genericResequencer<Tag | TagDraft>({
      source: options.source,
      position: options.target.sequence,
      items: tags,
    });

    const optimisticUpdate = normalizeTagList(updatedTags.list);

    if (options.save === false) {
      dispatch(updateTags({ entities: optimisticUpdate.entities, result: [] }));
      return;
    }

    try {
      const updatedTag = {
        ...options.source,
        ...updatedTags.sequenceItem,
      };

      dispatch(saveTag(updatedTag));
    } catch (err) {
      dispatch(
        showToast({
          message: 'Error when updating tag order',
          type: 'danger',
        })
      );
      dispatch(fetchTags());
      throw err;
    }
  };

export const toggleArchiveTag = (tagId: number): Thunk => {
  return async (dispatch, getState) => {
    const state = getState();
    const tagsEntities = getTagEntities(state);
    const tag = tagsEntities[tagId];
    // check if a parent exists and is inactive, if true then restore it
    if (tag.parent_id) {
      const parent = tagsEntities[tag.parent_id];
      if (!parent.active) {
        await dispatch(toggleArchiveTag(parent.id));
      }
    }

    try {
      const data = await dispatch(
        wrapSaving(tagsApi.save({ ...tag, active: !tag.active }), tagId.toString())
      );

      dispatch(savedTag(normalizeTag(data)));

      // Update all projects that have this tag marked as required
      const fetchProjectId = await getFetchProjectId();
      getAllLoadedProjects(state)
        .map(project => project.fullProject)
        .filter(notNull)
        .filter(project => project.required_label_ids?.includes(tagId))
        .map(project => void dispatch(fetchProjectId(project.id)));

      return data;
    } catch (err) {
      dispatch(
        showToast({
          message: `Error when updating tag "${tag?.name}" ${err.message}`,
          type: 'danger',
        })
      );

      dispatch(errorSavingTag(err));
    }
  };
};
